import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileCheck2,
  Filter,
  Calendar,
  Building2,
  RefreshCw,
  Search,
  Check,
  RotateCcw
} from 'lucide-react';
import { MarksEntryStatusModule } from '../components/MarksEntryStatusModule';
import { StudentAcademicHistoryModal } from '../components/StudentAcademicHistoryModal';

export const SubmissionStatusPage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState('August');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  const isBEOorDEO = user?.role === 'beo' || user?.role === 'deo' || user?.role === 'admin';

  const loadData = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        month: selectedMonth,
        block: selectedBlock
      });
      const res = await fetchWithAuth(`/api/submissions/status-grid?${query}`);
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedBlock]);

  const handleVerify = async (schoolId: number) => {
    try {
      const res = await fetchWithAuth('/api/monthly-assessments/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_id: schoolId, month: selectedMonth })
      });
      const json = await res.json();
      if (json.success) {
        setActionMessage('Submission approved & verified successfully.');
        loadData();
      }
    } catch (e) {}
  };

  const handleReturn = async (schoolId: number) => {
    try {
      const res = await fetchWithAuth('/api/monthly-assessments/return', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_id: schoolId, month: selectedMonth, remarks: 'Corrections needed in Class 10 marks roster' })
      });
      const json = await res.json();
      if (json.success) {
        setActionMessage('Submission returned to school for revision.');
        loadData();
      }
    } catch (e) {}
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
      case 'VERIFIED':
        return <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded text-[11px] border border-emerald-300">Verified</span>;
      case 'SUBMITTED':
        return <span className="bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded text-[11px] border border-blue-300">Submitted</span>;
      case 'DRAFT':
        return <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded text-[11px] border border-amber-300">Draft</span>;
      case 'REVISION_REQUIRED':
        return <span className="bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded text-[11px] border border-rose-300">Returned</span>;
      default:
        return <span className="bg-slate-100 text-slate-700 font-semibold px-2 py-0.5 rounded text-[11px] border border-slate-300">Not Started</span>;
    }
  };

  const filteredRecords = Array.isArray(data?.records)
    ? data.records.filter((r: any) =>
        (r?.school_name?.toLowerCase() ?? '').includes(search.toLowerCase()) ||
        (r?.school_code?.toLowerCase() ?? '').includes(search.toLowerCase()) ||
        (r?.block?.toLowerCase() ?? '').includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="space-y-6 pb-12">
      {/* Header Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
            District Assessment Compliance & Submissions Registry
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            Real-Time School Assessment Submission Monitoring
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            Status Indicators: Not Started • Draft • Submitted • Verified • Returned
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Month */}
          <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded text-xs">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="font-semibold text-slate-700">Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent font-bold text-[#0F2C59] focus:outline-none"
            >
              <option value="May">May</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
            </select>
          </div>

          {/* Block */}
          {user?.role !== 'school' && (
            <div className="flex items-center space-x-1.5 bg-slate-50 border border-slate-300 px-2.5 py-1.5 rounded text-xs">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <span className="font-semibold text-slate-700">Block:</span>
              <select
                value={selectedBlock}
                onChange={(e) => setSelectedBlock(e.target.value)}
                className="bg-transparent font-medium text-slate-900 focus:outline-none"
              >
                <option value="all">All Blocks</option>
                <option value="Nuh">Nuh</option>
                <option value="Nagina">Nagina</option>
                <option value="Taoru">Taoru</option>
                <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
                <option value="Punhana">Punhana</option>
              </select>
            </div>
          )}

          <button onClick={loadData} className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded text-slate-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-900 text-xs rounded font-semibold flex items-center justify-between">
          <span>{actionMessage}</span>
          <button onClick={() => setActionMessage(null)} className="text-emerald-700 font-bold">×</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-xs">
          <div className="text-[10px] font-bold text-slate-500 uppercase">Total Schools</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{data?.summary?.total || 0}</div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-xs">
          <div className="text-[10px] font-bold text-blue-700 uppercase">Submitted</div>
          <div className="text-xl font-bold text-blue-700 mt-1">{data?.summary?.submitted || 0}</div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-emerald-200 shadow-xs">
          <div className="text-[10px] font-bold text-emerald-700 uppercase">Verified</div>
          <div className="text-xl font-bold text-emerald-700 mt-1">{data?.summary?.verified || 0}</div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-amber-200 shadow-xs">
          <div className="text-[10px] font-bold text-amber-700 uppercase">Draft</div>
          <div className="text-xl font-bold text-amber-700 mt-1">{data?.summary?.draft || 0}</div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-rose-200 shadow-xs">
          <div className="text-[10px] font-bold text-rose-700 uppercase">Returned</div>
          <div className="text-xl font-bold text-rose-700 mt-1">{data?.summary?.returned || 0}</div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-indigo-200 shadow-xs">
          <div className="text-[10px] font-bold text-indigo-700 uppercase">Completion Rate</div>
          <div className="text-xl font-bold text-indigo-900 mt-1">{data?.summary?.completion_rate || 0}%</div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <div>
            <h2 className="text-sm font-bold text-[#0F2C59]">
              School Submission Grid ({selectedMonth} Assessment)
            </h2>
            <div className="text-xs text-slate-500">
              Showing {filteredRecords.length} school records
            </div>
          </div>

          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search school or block..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded bg-slate-50"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3.5">School Code</th>
                <th className="py-2.5 px-3.5">School Name</th>
                <th className="py-2.5 px-3">Block</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-right">Students</th>
                <th className="py-2.5 px-3 text-right">Appeared</th>
                <th className="py-2.5 px-3 text-right">Absent</th>
                <th className="py-2.5 px-3.5 text-center">Status</th>
                {isBEOorDEO && <th className="py-2.5 px-3.5 text-center">Officer Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {filteredRecords.map((r: any) => (
                <tr key={r.school_id} className="hover:bg-slate-50 transition">
                  <td className="py-2.5 px-3.5 font-bold text-slate-900">{r.school_code}</td>
                  <td className="py-2.5 px-3.5 font-bold text-[#0F2C59]">{r.school_name}</td>
                  <td className="py-2.5 px-3 text-slate-700">{r.block}</td>
                  <td className="py-2.5 px-3 text-slate-600">{r.category}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-slate-900">{(r.total_students || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-700 font-bold">{(r.appeared_students || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2.5 px-3 text-right text-rose-700 font-bold">{(r.absent_students || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2.5 px-3.5 text-center">{getStatusBadge(r.status)}</td>
                  {isBEOorDEO && (
                    <td className="py-2.5 px-3.5 text-center">
                      {r.status === 'SUBMITTED' ? (
                        <div className="flex items-center justify-center space-x-1.5">
                          <button
                            onClick={() => handleVerify(r.school_id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded text-[10px] font-bold flex items-center space-x-0.5"
                            title="Verify and Approve"
                          >
                            <Check className="w-3 h-3" />
                            <span>Verify</span>
                          </button>
                          <button
                            onClick={() => handleReturn(r.school_id)}
                            className="bg-rose-600 hover:bg-rose-700 text-white px-2 py-0.5 rounded text-[10px] font-bold flex items-center space-x-0.5"
                            title="Return for Revision"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>Return</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400 font-normal">No action pending</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Marks Entry Status Module by Subject & Class */}
      <div className="pt-4">
        <MarksEntryStatusModule
          defaultMonth={selectedMonth}
          defaultBlock={selectedBlock}
          title="School-Wise Marks Entry Status & Progress Matrix"
          onOpenStudent={(stId) => setSelectedStudentId(stId)}
        />
      </div>

      {/* Student Academic History Dossier Modal */}
      {selectedStudentId && (
        <StudentAcademicHistoryModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
    </div>
  );
};
